import { __decorate } from 'tslib';
import { DOCUMENT } from '@angular/common';
import { HttpResponse, HttpErrorResponse, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject, InjectionToken, NgModule, Optional } from '@angular/core';
import { Debounce, parseUrl, getPathClass } from '@js-dot/core';
import { BehaviorSubject } from 'rxjs';
import { tap, map, distinctUntilChanged } from 'rxjs/operators';
import * as i1 from '@ng-dot/core';
import { NgClassToggle } from '@ng-dot/core';
import * as i2 from '@ng-dot/route';
class DotHttpRequestInterceptor {
  _httpState;
  constructor(_httpState) {
    this._httpState = _httpState;
  }
  intercept(request, next) {
    const {
      _httpState
    } = this;
    _httpState.request();
    return next.handle(request).pipe(tap({
      next: event => {
        // event.type = 0 is cancel
        if (event instanceof HttpResponse || event.type === 0) {
          _httpState.response();
        }
      },
      error: event => {
        if (event instanceof HttpErrorResponse) {
          _httpState.response();
        }
      }
    }));
  }
  static ɵfac = function DotHttpRequestInterceptor_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotHttpRequestInterceptor)(i0.ɵɵinject(DotHttpStateService));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotHttpRequestInterceptor,
    factory: DotHttpRequestInterceptor.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotHttpRequestInterceptor, [{
    type: Injectable
  }], () => [{
    type: DotHttpStateService
  }], null);
})();
class DotHttpStateService {
  _classList;
  _isLoad$ = new BehaviorSubject(0);
  isLoad$;
  constructor(document) {
    this.isLoad$ = this._isLoad$.pipe(map(count => count > 0), distinctUntilChanged());
    const {
      classList
    } = document.documentElement;
    this._classList = classList;
    this.isLoad$.subscribe(loading => {
      if (loading) {
        classList.add('_loading');
      } else {
        this._loaded();
      }
    });
  }
  request() {
    const {
      _isLoad$
    } = this;
    _isLoad$.next(_isLoad$.value + 1);
  }
  response() {
    const {
      _isLoad$
    } = this;
    _isLoad$.next(Math.max(_isLoad$.value - 1, 0));
  }
  _loaded() {
    this._classList.remove('_loading');
    this._isLoad$.next(0);
  }
  static ɵfac = function DotHttpStateService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotHttpStateService)(i0.ɵɵinject(DOCUMENT));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotHttpStateService,
    factory: DotHttpStateService.ɵfac,
    providedIn: 'root'
  });
}
__decorate([Debounce(2000)], DotHttpStateService.prototype, "_loaded", null);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotHttpStateService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: HTMLDocument,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }], {
    _loaded: []
  });
})();
class DotRouteStateService {
  _render;
  _routeEvent;
  _documentElement;
  _lastClassNames;
  constructor(_render, _routeEvent, document) {
    this._render = _render;
    this._routeEvent = _routeEvent;
    this._documentElement = document.documentElement;
    _routeEvent.started$.subscribe(url => {
      if (url.indexOf('#') < 0) {
        const scroller = document.querySelector('[data-scroller]');
        if (scroller && scroller.dataset['scroller'] === 'true') {
          scroller.scrollTo(0, 0);
        } else {
          window.scrollTo(0, 0);
        }
      }
    });
    _routeEvent.ended$.subscribe(url => {
      const parsed = parseUrl(url);
      const {
        hash
      } = parsed;
      this._setPathClass(parsed.pathname);
      if (hash) {
        const target = document.getElementById(hash);
        if (target) {
          target.scrollIntoView();
        }
      }
    });
  }
  _setPathClass(path) {
    const {
      _render,
      _documentElement,
      _lastClassNames
    } = this;
    if (_lastClassNames) {
      _render.removeClass(_documentElement, _lastClassNames);
    }
    const classNames = getPathClass(path);
    _render.addClass(_documentElement, classNames);
    this._lastClassNames = classNames;
  }
  static ɵfac = function DotRouteStateService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRouteStateService)(i0.ɵɵinject(i1.DotRendererService), i0.ɵɵinject(i2.DotRouteEventService), i0.ɵɵinject(DOCUMENT));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotRouteStateService,
    factory: DotRouteStateService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRouteStateService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.DotRendererService
  }, {
    type: i2.DotRouteEventService
  }, {
    type: Document,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }], null);
})();
class DotScrollStateService {
  _scrollEvent;
  constructor(_scrollEvent, document) {
    this._scrollEvent = _scrollEvent;
    const {
      documentElement
    } = document;
    const ignoreTime = 240;
    new NgClassToggle(documentElement, '_scrolling').set(_scrollEvent.scrolling$);
    new NgClassToggle(documentElement, '_scrolled').set(_scrollEvent.scrolled$);
    new NgClassToggle(documentElement, '_scrolled-x').set(_scrollEvent.xScrolled$);
    new NgClassToggle(documentElement, '_scrolled-y').set(_scrollEvent.yScrolled$);
    new NgClassToggle(documentElement, '_scroll-up').set(_scrollEvent.up$);
    new NgClassToggle(documentElement, '_scroll-down').set(_scrollEvent.down$);
  }
  static ɵfac = function DotScrollStateService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotScrollStateService)(i0.ɵɵinject(i1.DotScrollEventService), i0.ɵɵinject(DOCUMENT));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotScrollStateService,
    factory: DotScrollStateService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotScrollStateService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.DotScrollEventService
  }, {
    type: Document,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }], null);
})();
const DOT_STATE_SERVICE = new InjectionToken('#ng-dot/ui/state/service');
class DotStateModule {
  static forRoot(...services) {
    return {
      ngModule: DotStateModule,
      providers: [{
        provide: DOT_STATE_SERVICE,
        useValue: services
      }]
    };
  }
  constructor(injector, services) {
    if (services) {
      let current = services.length;
      while (current-- > 0) {
        injector.get(services[current]);
      }
    }
  }
  static ɵfac = function DotStateModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotStateModule)(i0.ɵɵinject(i0.Injector), i0.ɵɵinject(DOT_STATE_SERVICE, 8));
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: DotStateModule
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    providers: [DotHttpRequestInterceptor, {
      provide: HTTP_INTERCEPTORS,
      useClass: DotHttpRequestInterceptor,
      multi: true
    }]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotStateModule, [{
    type: NgModule,
    args: [{
      providers: [DotHttpRequestInterceptor, {
        provide: HTTP_INTERCEPTORS,
        useClass: DotHttpRequestInterceptor,
        multi: true
      }]
    }]
  }], () => [{
    type: i0.Injector
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [DOT_STATE_SERVICE]
    }]
  }], null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { DOT_STATE_SERVICE, DotHttpRequestInterceptor, DotHttpStateService, DotRouteStateService, DotScrollStateService, DotStateModule };
